import { type FC, useRef, useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import type { GraphQLEmployee } from 'modules/org_chart/lib/employeeUtils'

interface Props {
  results: GraphQLEmployee[]
  onResultChange: (
    result: GraphQLEmployee | undefined,
    currentEmployee: GraphQLEmployee | undefined
  ) => void
  search: string
}

const AUTO_NAVIGATION_RESULTS_LIMIT = 25

const SearchResultsNavigator: FC<Props> = ({ results, search, onResultChange }) => {
  const [index, setIndex] = useState(-1)
  const autoNavigateTimeoutIdRef = useRef<number>()

  const clearAutoNavigateTimeout = () => {
    if (autoNavigateTimeoutIdRef.current === undefined) {
      return
    }

    window.clearTimeout(autoNavigateTimeoutIdRef.current)
    autoNavigateTimeoutIdRef.current = undefined
  }

  useUnsafeEffect(() => {
    clearAutoNavigateTimeout()
    setIndex(-1)

    if (!search) {
      return
    }

    if (results.length === 0 || results.length > AUTO_NAVIGATION_RESULTS_LIMIT) {
      onResultChange(undefined, undefined)
      return
    }

    autoNavigateTimeoutIdRef.current = window.setTimeout(() => {
      const firstResult = results[0]

      autoNavigateTimeoutIdRef.current = undefined

      if (!firstResult) {
        return
      }

      setIndex(0)
      onResultChange(firstResult, undefined)
    }, 150)

    return () => {
      clearAutoNavigateTimeout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, results[0]?.id, search])

  const handlePrevious = () => {
    const isFirstResult = index <= 0

    if (isFirstResult) {
      handleClick(results.length - 1)
    } else {
      handleClick(index - 1)
    }
  }

  const handleNext = () => {
    const isLastResult = index + 1 >= results.length

    if (isLastResult) {
      handleClick(0)
    } else {
      handleClick(index + 1)
    }
  }

  const handleClick = (nextIndex: number) => {
    const nextResult = results[nextIndex]

    if (!nextResult) {
      return
    }

    clearAutoNavigateTimeout()
    setIndex(nextIndex)
    onResultChange(nextResult, undefined)
  }

  return (
    <>
      {search && (
        <Box
          flexDirection="row"
          border={{
            all: {
              color: 'grey400',
              width: 's2',
              style: 'solid',
            },
          }}
          background="grey000White"
          borderRadius={{
            all: 'abs100',
          }}
          alignItems="center"
          justifyContent="flexEnd"
          paddingY="s4"
          paddingRight="s12"
          paddingLeft="s8"
          gap="s4"
        >
          <Box
            border={{
              right: {
                width: 's2',
                style: 'solid',
                color: 'grey400',
              },
            }}
          ></Box>
          <Button
            type="secondary"
            onClick={handlePrevious}
            disabled={!results.length}
            iconName="arrow-left"
          />
          <Text.Normal color={!results ? 'grey600' : 'grey1000'}>
            {i18n.t('employees.orgchart.results', {
              index: results.length && index >= 0 ? index + 1 : 0,
              length: results.length,
            })}
          </Text.Normal>
          <Box></Box>
          <Button
            type="secondary"
            onClick={handleNext}
            disabled={!results.length}
            iconName="arrow-right"
          />
        </Box>
      )}
    </>
  )
}

export default SearchResultsNavigator
